/**
 * Parte del día: lo que el despacho diario autonomo (cron 07:00) dejo escrito.
 *
 * Jerarquia deliberada: primero el resumen hablado —las 2-3 frases que
 * Androcles pronuncia al abrir sesion—, despues la cifra, despues las alertas.
 * El detalle tecnico no aparece: para eso esta la conversacion.
 */
import { Boton } from '@/components/ui/button';
import { Tarjeta, TarjetaCabecera, TarjetaTitulo, TarjetaContenido } from '@/components/ui/card';
import { formatearEuros, formatearFechaLarga, formatearHora, cn } from '@/lib/utils';
import type { AlertaParte, ParteDia } from '@/lib/api';

interface PropsParteDelDia {
  parte: ParteDia | null;
  cargando?: boolean;
  /** Pide a Androcles que lea el parte en voz alta (TTS). */
  alPedirLectura?: () => void;
  /** Lleva a la bandeja de Nivel 2 cuando hay propuestas esperando. */
  alIrABandeja?: () => void;
  className?: string;
}

export function ParteDelDia({
  parte,
  cargando = false,
  alPedirLectura,
  alIrABandeja,
  className,
}: PropsParteDelDia) {
  if (cargando) {
    return (
      <Tarjeta className={className}>
        <TarjetaContenido>
          <p className="text-lg text-muted-foreground">Recogiendo el parte de hoy…</p>
        </TarjetaContenido>
      </Tarjeta>
    );
  }

  if (!parte) {
    return (
      <Tarjeta className={className}>
        <TarjetaCabecera>
          <TarjetaTitulo>Parte del día</TarjetaTitulo>
        </TarjetaCabecera>
        <TarjetaContenido>
          <p className="text-lg text-muted-foreground">
            Todavía no hay parte de hoy. Lo preparo cada mañana a primera hora.
          </p>
        </TarjetaContenido>
      </Tarjeta>
    );
  }

  const subeSaldo = (parte.variacion_dia ?? 0) >= 0;

  return (
    <Tarjeta className={cn('animate-entrada-suave', className)}>
      <TarjetaCabecera>
        <TarjetaTitulo>Parte del {formatearFechaLarga(parte.fecha)}</TarjetaTitulo>
        <p className="text-sm text-muted-foreground">
          Preparado a las {formatearHora(parte.generado_en)}
        </p>
      </TarjetaCabecera>

      <TarjetaContenido className="flex flex-col gap-7">
        {/* Lo que Androcles dice de viva voz, palabra por palabra. */}
        {/* El resumen hablado se lee comodo sin gritar: antes text-2xl (36px)
            con interlineado suelto ocupaba media pantalla en el movil. A
            26-30px sigue siendo grande y claro para el jefe. */}
        <p className="max-w-[46ch] text-lg leading-snug sm:text-xl">{parte.resumen_hablado}</p>

        {/* La cifra grande solo se pinta si existe. Un "0,00 EUR" cuando no
            hay dato no es cero: es mentira, y el jefe decide con esto. */}
        {parte.posicion_consolidada != null && (
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
            <span className="cifra text-4xl font-bold tabular-nums">
              {formatearEuros(parte.posicion_consolidada)}
            </span>
            {parte.variacion_dia != null && (
              <span
                className={cn(
                  'cifra text-lg font-semibold',
                  subeSaldo ? 'text-[hsl(var(--cobro))]' : 'text-[hsl(var(--pago))]',
                )}
              >
                {formatearEuros(parte.variacion_dia, true)} desde ayer
              </span>
            )}
          </div>
        )}

        {parte.detalle_markdown && <DetalleParte markdown={parte.detalle_markdown} />}

        {parte.alertas.length > 0 && (
          <ul className="flex flex-col gap-4">
            {parte.alertas.map((alerta) => (
              <FilaAlerta key={alerta.id} alerta={alerta} />
            ))}
          </ul>
        )}

        <div className="flex flex-col gap-4 sm:flex-row">
          {alPedirLectura && (
            <Boton variante="secundario" className="flex-1" onClick={alPedirLectura}>
              Léemelo en voz alta
            </Boton>
          )}
          {parte.confirmaciones_pendientes > 0 && alIrABandeja && (
            <Boton variante="principal" className="flex-1" onClick={alIrABandeja}>
              Ver {parte.confirmaciones_pendientes} propuesta
              {parte.confirmaciones_pendientes === 1 ? '' : 's'}
            </Boton>
          )}
        </div>
      </TarjetaContenido>
    </Tarjeta>
  );
}

/**
 * Detalle del parte, escrito por el despacho en Markdown.
 *
 * Se interpreta el subconjunto EXACTO que produce `_redactar_parte()`:
 * secciones `###`, viñetas `- `, la nota `> _…_` del modo consulta y cifras en
 * `**negrita**`. Cualquier otra linea se pinta como parrafo.
 *
 * Deliberadamente NO se usa una libreria de Markdown ni `dangerouslySetInnerHTML`:
 * en el detalle van conceptos bancarios (dato NO confiable), asi que todo sale
 * como nodos de texto de React. Lo unico que se "interpreta" es el estilo.
 *
 * El `## Parte del <fecha>` de la primera linea se omite: ya es el titulo de la
 * tarjeta y repetirlo sobraba.
 */
function DetalleParte({ markdown }: { markdown: string }) {
  const bloques: JSX.Element[] = [];
  let viñetas: string[] = [];

  /** Vuelca las viñetas acumuladas como una lista y limpia el buffer. */
  const cerrarLista = () => {
    if (viñetas.length === 0) return;
    const items = viñetas;
    viñetas = [];
    bloques.push(
      <ul key={`lista-${bloques.length}`} className="flex flex-col gap-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3 text-base leading-relaxed">
            <span className="text-muted-foreground" aria-hidden="true">
              ·
            </span>
            <span>{conNegritas(item)}</span>
          </li>
        ))}
      </ul>,
    );
  };

  for (const bruta of markdown.split('\n')) {
    const linea = bruta.trim();
    if (!linea) {
      cerrarLista();
      continue;
    }
    if (linea.startsWith('## ') && !linea.startsWith('### ')) {
      continue; // Titulo del parte: ya esta en la cabecera de la tarjeta.
    }
    if (linea.startsWith('### ')) {
      cerrarLista();
      bloques.push(
        <h3 key={`h-${bloques.length}`} className="text-lg font-semibold">
          {linea.slice(4)}
        </h3>,
      );
      continue;
    }
    if (linea.startsWith('> ')) {
      cerrarLista();
      bloques.push(
        <p
          key={`nota-${bloques.length}`}
          className="rounded-md border-l-4 border-border bg-muted/40 px-5 py-3 text-base italic text-muted-foreground"
        >
          {linea.slice(2).replace(/^_|_$/g, '')}
        </p>,
      );
      continue;
    }
    if (linea.startsWith('- ')) {
      viñetas.push(linea.slice(2));
      continue;
    }
    cerrarLista();
    bloques.push(
      <p key={`p-${bloques.length}`} className="text-base leading-relaxed">
        {conNegritas(linea)}
      </p>,
    );
  }
  cerrarLista();

  return <div className="flex flex-col gap-5">{bloques}</div>;
}

/** Convierte los `**tramos**` en <strong>, dejando el resto como texto plano. */
function conNegritas(texto: string) {
  return texto.split(/(\*\*[^*]+\*\*)/g).map((trozo, i) =>
    trozo.startsWith('**') && trozo.endsWith('**') && trozo.length > 4 ? (
      <strong key={i} className="cifra font-semibold">
        {trozo.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{trozo}</span>
    ),
  );
}

/** Alerta del parte. La severidad se ve por color Y se dice con palabras. */
function FilaAlerta({ alerta }: { alerta: AlertaParte }) {
  const estilo: Record<AlertaParte['severidad'], string> = {
    informativa: 'border-border',
    atencion: 'border-[hsl(var(--alerta))]',
    critica: 'border-destructive',
  };
  const etiqueta: Record<AlertaParte['severidad'], string> = {
    informativa: 'Para su informacion',
    atencion: 'Ojo',
    critica: 'Urgente',
  };

  return (
    <li className={cn('rounded-lg border-2 p-5', estilo[alerta.severidad])}>
      <p className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
        {etiqueta[alerta.severidad]}
      </p>
      <p className="mt-1 text-lg font-semibold">{alerta.titulo}</p>
      <p className="mt-2 text-base leading-relaxed">{alerta.detalle}</p>
      {alerta.fecha_referencia && (
        <p className="mt-2 text-sm text-muted-foreground">
          Fecha clave: {formatearFechaLarga(alerta.fecha_referencia)}
        </p>
      )}
    </li>
  );
}
